import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_PRODUCT_API ?? '/api/v1';

export type ProductStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'published' | 'unpublished' | 'archived';
export type ProductType = 'physical' | 'digital' | 'bundle' | 'service' | 'subscription';
export type Visibility = 'public' | 'private' | 'internal' | 'hidden';

export type Barcodes = { ean?: string; upc?: string; isbn?: string; asin?: string; gtin?: string };

export type ProductIdentity = {
  code?: string;
  sku?: string;
  internal_sku?: string;
  barcodes?: Barcodes;
};

export type ProductProfile = {
  short_description?: string;
  long_description?: string;
  title?: string;
  category?: string;
  subcategory?: string;
  image_url?: string;
};

export type Product = {
  id: string;
  tenant_id: string;
  organization_id: string;
  workspace_id?: string;
  name: string;
  description?: string;
  brand?: string;
  manufacturer?: string;
  country_of_origin?: string;
  type: ProductType;
  condition: string;
  status: ProductStatus;
  visibility: Visibility;
  lifecycle: string;
  identity: ProductIdentity;
  profile: ProductProfile;
  metadata?: Record<string, string>;
  tags?: string[];
  version: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  approved_at?: string;
};

export type ActivityEntry = { id: string; action: string; summary: string; created_at: string };
export type AuditEntry = { id: string; action: string; outcome: string; created_at: string };
export type HealthSnapshot = { product_id: string; score: number; status: string; issues?: string[]; checks?: Record<string, boolean> };
export type ScoreSnapshot = { product_id: string; completeness_score: number; quality_score: number; health_score: number; overall_score: number; missing_fields?: string[] };
export type ProductDashboard = { stats?: Record<string, unknown>; widgets?: Array<{ id: string; title: string; type: string }> };
export type Relationship = { id: string; product_id: string; related_id: string; type: string; created_at: string };

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function prodFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) throw new Error(`Products ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const productApi = {
  list: (params?: { status?: string; q?: string; sku?: string; brand?: string; type?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    if (params?.sku) qs.set('sku', params.sku);
    if (params?.brand) qs.set('brand', params.brand);
    if (params?.type) qs.set('type', params.type);
    const query = qs.toString();
    return prodFetch<Product[]>(`/products${query ? `?${query}` : ''}`);
  },
  search: (q: string) => prodFetch<{ results: Product[]; count: number }>(`/products/search?q=${encodeURIComponent(q)}`),
  get: (id: string) => prodFetch<Product>(`/products/${id}`),
  create: (input: Partial<Product> & { name: string; identity?: ProductIdentity }) =>
    prodFetch<Product>('/products', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<Product>) =>
    prodFetch<Product>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  patch: (id: string, input: Partial<Product>) =>
    prodFetch<Product>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  delete: (id: string) => prodFetch<void>(`/products/${id}`, { method: 'DELETE' }),
  archive: (id: string) => prodFetch<Product>(`/products/${id}/archive`, { method: 'POST' }),
  restore: (id: string) => prodFetch<Product>(`/products/${id}/restore`, { method: 'POST' }),
  clone: (id: string, input?: { name?: string; sku?: string }) =>
    prodFetch<Product>(`/products/${id}/clone`, { method: 'POST', body: JSON.stringify(input ?? {}) }),
  approve: (id: string, input?: { reject?: boolean; comment?: string }) =>
    prodFetch<Product>(`/products/${id}/approve`, { method: 'POST', body: JSON.stringify(input ?? {}) }),
  publish: (id: string) => prodFetch<Product>(`/products/${id}/publish`, { method: 'POST' }),
  activity: (id: string) => prodFetch<ActivityEntry[]>(`/products/${id}/activity`),
  audit: (id: string) => prodFetch<AuditEntry[]>(`/products/${id}/audit`),
  health: (id: string) => prodFetch<HealthSnapshot>(`/products/${id}/health`),
  score: (id: string) => prodFetch<ScoreSnapshot>(`/products/${id}/score`),
  dashboard: () => prodFetch<ProductDashboard>('/products/dashboard'),
  relationships: (id: string) => prodFetch<Relationship[]>(`/products/${id}/relationships`),
  addRelationship: (id: string, input: { related_id: string; type: string }) =>
    prodFetch<Relationship>(`/products/${id}/relationships`, { method: 'POST', body: JSON.stringify(input) }),
};
